import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BlockBlobClient } from '@azure/storage-blob';
import { DomainType, DomainUtility, getExtension, getExtensionFromMimeType, tryCatch } from '@jms/model';
import { UtilService } from '@jms/utils/util.services';
import { Observable, lastValueFrom } from 'rxjs';
import { v4 } from 'uuid';

export interface AzureUploadResult {
    success: boolean;
    url?: string;
    error?: any;
}

export class AzureUploadItem {

    private _progress: number = 0;
    public get progress() {
        return this._progress;
    }

    private _blobName: string;
    public get blobName() {
        return this._blobName;
    }

    constructor(
        public file: Blob | Buffer | ArrayBuffer | ArrayBufferView,
        public fileName: string,
        public fileSize: number,
        public mimeType: string,
        public abortSignal?: AbortSignal,
    ) {
        const extension = fileName ? getExtension(fileName) : getExtensionFromMimeType(mimeType);
        this._blobName = `${v4()}.${extension}`
    }

    setProgress(progress: number) {
        this._progress = progress;
    }

}

@Injectable({ providedIn: 'root' })
export class AzureSasProvider {

    constructor(
        private http: HttpClient
    ) { }

    public getSas(filename: string, type: 'Add' | 'Delete'): Promise<string> {

        const domain = UtilService.getLocation().hostName;
        const isContractor = DomainUtility.getDomainType(domain) === DomainType.ContractorPortal;
        let baseUrl = '';

        if (isContractor) {
            baseUrl = `https://__tenant__`
        }

        const storageAccount = 'https://jmsfiles.blob.core.windows.net';
        const blobUri = encodeURIComponent(`${storageAccount}/${filename}`);

        const url = type == 'Delete'
            ? `${baseUrl}/api/public/form/response/delete/sas?bloburi=${blobUri}`
            : `${baseUrl}/api/public/form/response/sas?bloburi=${blobUri}`;

        let sas$: Observable<string>;

        switch (type) {
            case 'Add':
                sas$ = this.http
                    .post(url, null, { responseType: 'text' });
                break;

            case 'Delete':
                sas$ = this.http
                    .delete(url, { responseType: 'text' });
                break;

            default:
                return null;
        }

        if (!sas$) return null;

        return lastValueFrom(sas$);

    }

}

@Injectable({ providedIn: 'root' })
export class AzureUploadService {

    public static readonly BlockSize = 4 * 1024 * 1024;
    public static readonly Concurrency = 10;

    constructor(
        private http: HttpClient,
        private sasProvider: AzureSasProvider
    ) { }

    public async upload(item: AzureUploadItem): Promise<AzureUploadResult> {

        const [sasError, sas] = await tryCatch(this.sasProvider.getSas(item.blobName, 'Add'));

        if (sasError) return null;

        const blockBlobClient = new BlockBlobClient(sas);

        const [uploadError, result] = await tryCatch(blockBlobClient
            .uploadData(item.file, {
                blockSize: AzureUploadService.BlockSize,
                concurrency: AzureUploadService.Concurrency,
                abortSignal: item.abortSignal,

                blobHTTPHeaders: {
                    blobContentType: item.mimeType,
                },
                metadata: {
                    'qqfilename': encodeURI(item.fileName)
                },
                onProgress: (ev) => {
                    item.setProgress(Math.floor((ev.loadedBytes / item.fileSize) * 100));
                },
            })
        );

        if (uploadError) {

            return {
                success: false,
                error: uploadError
            } as AzureUploadResult;

        }

        return {
            success: true,
            error: null,
            url: sas.split('?')[0]
        }
    }
}